import { type FC, useCallback, useMemo, useState, useEffect } from 'react';
import { useDebounce } from 'use-debounce';
import { TableCell, TableRow } from './ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Combobox, type ComboboxOption } from './ui/combobox';
import { MultiSelectCombobox } from './ui/multi-select-combobox';
import { X } from 'lucide-react';
import type { ComparatorOption } from '../utils/filterComparators';
import { comparatorOptions as defaultComparatorOptions } from '../utils/filterComparators';
import type { FilterEntry } from './FilterTable';

// Comparators that don't require a value input
const COMPARATORS_WITHOUT_VALUES = ['EMPTY', 'NOT_EMPTY', 'VALID_DATE', 'INVALID_DATE', 'VALID_NUMBER', 'INVALID_NUMBER'];

// Comparators that support multiple values
const MULTI_VALUE_COMPARATORS = ['IN'];

interface FilterRowProps {
  filter: FilterEntry;
  onChange: (newFilter: FilterEntry) => void;
  onDelete: () => void;
  columnOptions: ComboboxOption[];
  showComparator?: boolean;
  comparatorOptions?: ComparatorOption[];
  defaultComparator?: string;
  valueMode?: 'text' | 'multiselect';
  onFetchValueOptions?: (columnId: string, query: string) => Promise<string[]>;
  isLoadingValues?: boolean;
  valueOptions?: { value: string; label: string }[];
  onValueSearchChange?: (columnId: string, query: string) => void;
  isReadOnly?: boolean;
  getColumnLabel?: (columnId: string) => string | undefined;
}

export const FilterRow: FC<FilterRowProps> = ({
  filter,
  onChange,
  onDelete,
  columnOptions,
  showComparator = true,
  comparatorOptions = defaultComparatorOptions,
  defaultComparator = 'EQUALS',
  valueMode = 'text',
  onFetchValueOptions,
  isLoadingValues = false,
  valueOptions: externalValueOptions = [],
  onValueSearchChange,
  isReadOnly = false,
  getColumnLabel,
}) => {
  const [query, setQuery] = useState<string>('');
  const [debouncedQuery] = useDebounce(query, 500);
  const [localValueOptions, setLocalValueOptions] = useState<{ value: string; label: string }[]>([]);
  const [localIsLoading, setLocalIsLoading] = useState(false);

  const comparator = filter.comparator || defaultComparator;
  const requiresValue = !COMPARATORS_WITHOUT_VALUES.includes(comparator);
  const supportsMultipleValues = MULTI_VALUE_COMPARATORS.includes(comparator);

  // Fetch value options when query changes (for internal fetching)
  useEffect(() => {
    if (valueMode === 'multiselect' && onFetchValueOptions && filter.columnId && debouncedQuery !== undefined) {
      setLocalIsLoading(true);
      onFetchValueOptions(filter.columnId, debouncedQuery)
        .then((values) => {
          setLocalValueOptions(
            values.map((v) => ({
              value: v,
              label: v.length > 50 ? `${v.slice(0, 50)}...` : v,
            }))
          );
        })
        .finally(() => {
          setLocalIsLoading(false);
        });
    }
  }, [filter.columnId, debouncedQuery, onFetchValueOptions, valueMode]);

  // Notify parent of query change (for external fetching)
  useEffect(() => {
    if (onValueSearchChange && filter.columnId) {
      onValueSearchChange(filter.columnId, debouncedQuery);
    }
  }, [filter.columnId, debouncedQuery, onValueSearchChange]);

  // Use external value options if provided, otherwise use local
  const valueOptions = useMemo(() => {
    const options = externalValueOptions.length > 0 ? externalValueOptions : localValueOptions;
    const seenValues = new Set(options.map((o) => o.value));

    // Include currently selected values that aren't in the options
    const selectedValues = Array.isArray(filter.value) ? filter.value : filter.value ? [filter.value] : [];
    for (const v of selectedValues) {
      if (!seenValues.has(v)) {
        options.push({
          value: v,
          label: v.length > 50 ? `${v.slice(0, 50)}...` : v,
        });
      }
    }

    return options;
  }, [externalValueOptions, localValueOptions, filter.value]);

  const isLoading = isLoadingValues || localIsLoading;

  // Convert filter value to display formats
  const valueForInput = useMemo(() => {
    if (!requiresValue || supportsMultipleValues) {
      return '';
    }
    if (!filter.value) {
      return '';
    }
    if (Array.isArray(filter.value)) {
      return filter.value.length ? String(filter.value[0]) : '';
    }
    return String(filter.value);
  }, [filter.value, requiresValue, supportsMultipleValues]);

  const valueForMultiInput = useMemo(() => {
    if (!supportsMultipleValues) {
      return '';
    }
    if (!filter.value) {
      return '';
    }
    if (Array.isArray(filter.value)) {
      return filter.value.join(', ');
    }
    return String(filter.value);
  }, [filter.value, supportsMultipleValues]);

  const selectedValues = useMemo(() => {
    if (!filter.value) return [];
    if (Array.isArray(filter.value)) {
      return filter.value;
    }
    return [filter.value];
  }, [filter.value]);

  // Get display value for read-only mode
  const displayValue = useMemo(() => {
    if (!filter.value) return '';
    if (Array.isArray(filter.value)) {
      return filter.value.join(', ');
    }
    return String(filter.value);
  }, [filter.value]);

  // Get comparator label for display
  const comparatorLabel = useMemo(() => {
    if (comparator === 'IN') return null;
    const option = comparatorOptions.find((opt) => opt.value === comparator);
    return option ? option.label : comparator.replace('_', ' ');
  }, [comparator, comparatorOptions]);

  const handleColumnChange = useCallback(
    (newVal: string) => {
      // Reset value when column changes
      onChange({
        ...filter,
        columnId: newVal,
        value: supportsMultipleValues ? [] : '',
      });
      setQuery('');
    },
    [filter, onChange, supportsMultipleValues]
  );

  const handleComparatorChange = useCallback(
    (newComparator: string) => {
      const noValueComparators = new Set(COMPARATORS_WITHOUT_VALUES);
      const multiValueComparators = new Set(MULTI_VALUE_COMPARATORS);

      let nextValue: string | string[] | undefined;
      if (multiValueComparators.has(newComparator)) {
        const currentValue = filter.value;
        if (Array.isArray(currentValue)) {
          nextValue = currentValue;
        } else if (typeof currentValue === 'string' && currentValue.length) {
          nextValue = [currentValue];
        } else {
          nextValue = [];
        }
      } else if (noValueComparators.has(newComparator)) {
        nextValue = undefined;
      } else {
        const currentValue = filter.value;
        if (Array.isArray(currentValue)) {
          nextValue = currentValue.length ? String(currentValue[0]) : '';
        } else if (typeof currentValue === 'string') {
          nextValue = currentValue;
        } else if (currentValue != null) {
          nextValue = String(currentValue);
        } else {
          nextValue = '';
        }
      }

      onChange({
        ...filter,
        comparator: newComparator,
        value: nextValue,
      });
    },
    [filter, onChange]
  );

  const handleValueChange = useCallback(
    (newValue: string) => {
      onChange({
        ...filter,
        value: newValue,
      });
    },
    [filter, onChange]
  );

  const handleMultiValueTextChange = useCallback(
    (newValue: string) => {
      // Split values on comma, but retain trailing space in value chunks
      let splitVals = newValue.split(',').map((v) => v.replace(/^\s+/, ''));
      // If the last character is a comma, add an empty string to preserve room for the next entry
      if (newValue.endsWith(',')) {
        splitVals.push('');
      }
      // Remove all empty strings except at the very end
      while (splitVals.length > 1 && splitVals[splitVals.length - 2] === '' && splitVals[splitVals.length - 1] === '') {
        splitVals.pop();
      }
      onChange({
        ...filter,
        value: splitVals,
      });
    },
    [filter, onChange]
  );

  const handleMultiSelectChange = useCallback(
    (values: string[]) => {
      onChange({
        ...filter,
        value: values,
      });
    },
    [filter, onChange]
  );

  // Render read-only mode
  if (isReadOnly) {
    const columnLabel = getColumnLabel?.(filter.columnId) ||
      columnOptions.find((opt) => opt.value === filter.columnId)?.label ||
      filter.columnId;

    return (
      <TableRow>
        <TableCell>
          <div className="px-2 py-1.5 text-sm text-muted-foreground">
            {columnLabel}
          </div>
        </TableCell>
        {showComparator && (
          <TableCell>
            <span className="px-2 py-1 text-xs rounded-md bg-warning/20 text-warning-foreground">
              {comparatorLabel || comparator.replace('_', ' ')}
            </span>
          </TableCell>
        )}
        <TableCell>
          <div className="flex items-center gap-2">
            {!showComparator && comparatorLabel && (
              <span className="px-2 py-1 text-xs rounded-md bg-warning/20 text-warning-foreground">
                {comparatorLabel}
              </span>
            )}
            {requiresValue && displayValue && (
              <span className="px-2 py-1 text-xs rounded-md bg-muted text-muted-foreground">
                {displayValue}
              </span>
            )}
          </div>
        </TableCell>
        <TableCell className="text-right">
          <Button variant="ghost" size="sm" onClick={onDelete} className="h-8 w-8 p-0">
            <X className="h-4 w-4" />
          </Button>
        </TableCell>
      </TableRow>
    );
  }

  // Render editable mode
  return (
    <TableRow>
      <TableCell>
        <Combobox
          options={columnOptions}
          value={filter.columnId || ''}
          onValueChange={handleColumnChange}
          placeholder="Select..."
          emptyText="No options available."
        />
      </TableCell>
      {showComparator && (
        <TableCell>
          <Select value={comparator} onValueChange={handleComparatorChange}>
            <SelectTrigger>
              <SelectValue placeholder="Select comparator..." />
            </SelectTrigger>
            <SelectContent>
              {comparatorOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </TableCell>
      )}
      <TableCell>
        <div className="flex items-center gap-2">
          {!requiresValue && (
            <span className="px-2 py-1 text-xs rounded-md bg-warning/20 text-warning-foreground">
              {comparator === 'EMPTY'
                ? 'Empty'
                : comparator === 'NOT_EMPTY'
                ? 'Not Empty'
                : comparator.replace('_', ' ')}
            </span>
          )}
          {requiresValue && supportsMultipleValues && valueMode === 'multiselect' && (
            <MultiSelectCombobox
              options={valueOptions}
              value={selectedValues}
              onValueChange={handleMultiSelectChange}
              placeholder="Select values..."
              emptyText="No values available."
              onInputChange={setQuery}
              isLoading={isLoading}
              className="flex-1"
            />
          )}
          {requiresValue && supportsMultipleValues && valueMode === 'text' && (
            <Input
              type="text"
              value={valueForMultiInput}
              onChange={(e) => handleMultiValueTextChange(e.target.value)}
              placeholder="Comma-separated values..."
              className="flex-1"
            />
          )}
          {requiresValue && !supportsMultipleValues && (
            <Input
              type="text"
              value={valueForInput}
              onChange={(e) => handleValueChange(e.target.value)}
              placeholder="Enter value..."
              className="flex-1"
            />
          )}
        </div>
      </TableCell>
      <TableCell className="text-right">
        <Button variant="ghost" size="sm" onClick={onDelete} className="h-8 w-8 p-0">
          <X className="h-4 w-4" />
        </Button>
      </TableCell>
    </TableRow>
  );
};
